import {
  Box,
  Checkbox,
  HStack,
  IconButton,
  Spacer,
  Stack,
} from '@cardboard-ui/react';
import {
  CloseIcon,
  EditIcon,
  EllipsisHIcon,
  NavigateForwardIcon,
  TrashIcon,
} from 'components/icons';
import React, { PropsWithChildren } from 'react';

export type ListItemActionVariant =
  | 'default'
  | 'navigation'
  | 'options'
  | 'removal'
  | 'check'
  | 'edit'
  | 'delete';

export interface ListItemProps {
  variant?: ListItemActionVariant;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  ariaLabel?: string;
  isChecked?: boolean;
  onClick?(): void;
  onRightButtonClick?(): void;
  rightButtonAriaLabel?: string;
  isRightButtonLoading?: boolean;
}

export const ListItem = ({
  variant,
  icon,
  badge,
  ariaLabel,
  rightButtonAriaLabel,
  isChecked,
  onClick,
  onRightButtonClick,
  isRightButtonLoading,
  children,
}: PropsWithChildren<ListItemProps>) => {
  const isClickable = variant && variant !== 'default';
  const clickHandler: React.MouseEventHandler<HTMLDivElement> = (e) => {
    onClick && onClick();
  };
  return (
    <HStack
      w="100%"
      cursor={isClickable ? 'pointer' : 'default'}
      onClick={clickHandler}
      aria-label={ariaLabel}
      padding="10px 20px"
      spacing={2}
    >
      <HStack spacing={4} minWidth={0} flexGrow={1} whiteSpace="nowrap">
        {icon && <Box>{icon}</Box>}
        <Stack spacing={0} whiteSpace="nowrap" minWidth={0}>
          {children}
        </Stack>
      </HStack>
      <Spacer />
      <HStack spacing={1}>
        <Box>{badge}</Box>
        {variant === 'check' ? (
          <Checkbox isChecked={isChecked} onClick={clickHandler} />
        ) : (
          <ListItemActionButton
            variant={variant || 'default'}
            ariaLabel={rightButtonAriaLabel || ariaLabel}
            onClick={onRightButtonClick || onClick}
            isLoading={isRightButtonLoading}
          />
        )}
      </HStack>
    </HStack>
  );
};

interface ListItemActionButtonProps {
  variant: ListItemActionVariant;
  ariaLabel?: string;
  onClick?(): void;
  isLoading?: boolean;
}

const ListItemActionButton = ({
  variant,
  ariaLabel,
  onClick,
  isLoading,
}: ListItemActionButtonProps) => {
  if (variant === 'default' || !ariaLabel) {
    return null;
  }

  let actionIcon = <NavigateForwardIcon />;
  if (variant === 'options') {
    actionIcon = <EllipsisHIcon />;
  } else if (variant === 'removal') {
    actionIcon = <CloseIcon />;
  } else if (variant === 'edit') {
    actionIcon = <EditIcon />;
  } else if (variant === 'delete') {
    actionIcon = <TrashIcon />;
  }
  return (
    <IconButton
      variant="link"
      aria-label={ariaLabel}
      color="blue.500"
      size="md"
      onClick={onClick}
      isLoading={isLoading}
    >
      {actionIcon}
    </IconButton>
  );
};
